import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import Swal from "sweetalert2";
import { MDBBadge, MDBBtn, MDBCard, MDBCardBody, MDBIcon } from "mdbreact";
import { axioKit, fullName } from "../../../../../services/utilities";
import OrderItems from "./orderItems";
import {
  PURCHASE_URL,
  formatDate,
  lineName,
  poNumber,
} from "../../../laboratory/purchaseOrders/common";

// days before the expiry a batch is flagged
const EXPIRING_DAYS = 30;

const STATUS = {
  available: { label: "Available", color: "success", icon: "check-circle" },
  expiring: { label: "Expiring", color: "warning", icon: "hourglass-half" },
  expired: { label: "Expired", color: "danger", icon: "skull-crossbones" },
  consumed: { label: "Consumed", color: "light", icon: "box-open" },
};

const FILTERS = [
  { key: "", label: "All" },
  { key: "available", label: "With stock" },
  { key: "expiring", label: "Expiring" },
  { key: "expired", label: "Expired" },
  { key: "consumed", label: "Consumed" },
];

// status of a batch from what is left and its expiry
const statusOf = ({ available, expiry }) => {
  if (!(available > 0)) return "consumed";
  if (!expiry) return "available";
  const days = (new Date(expiry) - new Date()) / 86400000;
  if (days < 0) return "expired";
  if (days <= EXPIRING_DAYS) return "expiring";
  return "available";
};

// worst status first, used for the item and the P.O.
const RANK = ["expired", "expiring", "available", "consumed"];
const worstOf = (statuses = []) =>
  RANK.find((status) => statuses.includes(status)) || "consumed";

const sum = (list, key) => list.reduce((total, b) => total + (b[key] || 0), 0);

/**
 * Inventory of every received P.O.: per item / lot, how much was received,
 * consumed and is left, and whether it expired.
 */
export default function PurchaseTracking() {
  const { token, activePlatform } = useSelector(({ auth }) => auth),
    [orders, setOrders] = useState([]),
    [year, setYear] = useState(String(new Date().getFullYear())),
    [filter, setFilter] = useState(""),
    [search, setSearch] = useState(""),
    [expanded, setExpanded] = useState({}),
    [isLoading, setIsLoading] = useState(false),
    // the P.O. being regenerated
    [regenerating, setRegenerating] = useState(null),
    { addToast } = useToasts();

  const branch = activePlatform?.branchId;

  const load = useCallback(() => {
    if (!branch || !token) return;
    setIsLoading(true);
    axioKit
      .universal(`${PURCHASE_URL}/tracking`, token, { branch })
      .then(({ payload }) => setOrders(payload || []))
      .catch((error) =>
        addToast(error.message, { appearance: "error", autoDismiss: true }),
      )
      .finally(() => setIsLoading(false));
  }, [branch, token, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const yearOf = ({ received, createdAt }) =>
    String(new Date(received || createdAt).getFullYear());

  const years = useMemo(
    () =>
      [
        ...new Set([String(new Date().getFullYear()), ...orders.map(yearOf)]),
      ].sort((a, b) => b - a),
    [orders],
  );

  // every P.O. with the status of its batches, items and itself
  const tracked = useMemo(
    () =>
      orders.map((order) => {
        const items = order.items.map((item) => {
          const batches = item.batches.map((batch) => ({
            ...batch,
            status: statusOf(batch),
          }));
          return {
            ...item,
            batches,
            status: worstOf(batches.map(({ status }) => status)),
          };
        });
        const batches = items.flatMap(({ batches: b }) => b);
        return {
          ...order,
          items,
          status: worstOf(items.map(({ status }) => status)),
          qty: sum(batches, "qty"),
          available: sum(batches, "available"),
          expired: sum(
            batches.filter(({ status }) => status === "expired"),
            "available",
          ),
        };
      }),
    [orders],
  );

  const ofYear = useMemo(
    () => tracked.filter((order) => !year || yearOf(order) === year),
    [tracked, year],
  );

  const counts = useMemo(
    () =>
      FILTERS.reduce((result, { key }) => {
        result[key] = key
          ? ofYear.filter(({ status }) => status === key).length
          : ofYear.length;
        return result;
      }, {}),
    [ofYear],
  );

  // P.O. number (with or without "PO-"), item or lot number
  const visible = useMemo(() => {
    const key = search.trim().toLowerCase().replace(/^po-?/, "");
    return ofYear
      .filter(({ status }) => !filter || status === filter)
      .filter(
        (order) =>
          !key ||
          poNumber(order).toLowerCase().includes(key) ||
          order.items.some(
            (item) =>
              lineName(item).toLowerCase().includes(key) ||
              item.batches.some(({ lot }) =>
                (lot || "").toLowerCase().includes(key),
              ),
          ),
      );
  }, [ofYear, filter, search]);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  /**
   * Rebuilds the inventory of a received P.O. when it went wrong: pack size
   * from the breakdown, missing packs, counts from the dispense logs.
   */
  const handleRegenerate = async (order) => {
    const { isConfirmed } = await Swal.fire({
      title: `Regenerate ${poNumber(order)}?`,
      html: "<small>Corrects the inventory of this P.O. from its records:<br/>the pack size from the product breakdown, the missing packs, and what was used from the dispense logs.<br/>Nothing is removed.</small>",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Regenerate",
    });
    if (!isConfirmed) return;

    setRegenerating(order._id);
    try {
      const { success } = await axioKit.update(
        PURCHASE_URL,
        { _id: order._id },
        token,
        "regenerate",
      );
      addToast(success, { appearance: "success", autoDismiss: true });
      load();
    } catch (error) {
      addToast(error.message, { appearance: "error", autoDismiss: true });
    } finally {
      setRegenerating(null);
    }
  };

  return (
    <MDBCard narrow className="pb-3">
      <MDBCardBody>
        {/* header */}
        <div className="mb-3">
          <div>
            <h5 className="mb-0">
              <MDBIcon icon="map-marker-alt" className="mr-2 text-primary" />
              Purchase Tracking
            </h5>
            <small className="text-muted">
              What is left of every received P.O. Usage is counted first-expiry,
              first-out, since dispensing is recorded per brand and variant.
            </small>
          </div>
        </div>

        {/* filters */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div className="btn-group flex-wrap" role="group" aria-label="Status">
            {FILTERS.map(({ key, label }) => (
              <button
                key={key || "all"}
                type="button"
                className={`btn btn-sm m-0 z-depth-0 ${
                  filter === key ? "btn-primary" : "btn-outline-primary"
                }`}
                style={{ textTransform: "none" }}
                onClick={() => setFilter(key)}
              >
                {label}
                {counts[key] > 0 && (
                  <MDBBadge
                    color={filter === key ? "light" : "primary"}
                    className="ml-2"
                  >
                    {counts[key]}
                  </MDBBadge>
                )}
              </button>
            ))}
          </div>
          <div className="d-flex align-items-center">
            <div className="position-relative mr-3" style={{ width: 240 }}>
              <MDBIcon
                icon="search"
                className="position-absolute text-muted"
                style={{ top: "50%", left: 10, transform: "translateY(-50%)" }}
              />
              <input
                className="form-control form-control-sm"
                style={{ paddingLeft: 30, paddingRight: 28 }}
                placeholder="Search P.O. no., item or lot"
                value={search}
                onChange={({ target }) => setSearch(target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="btn btn-link position-absolute p-0 m-0 text-muted"
                  style={{
                    top: "50%",
                    right: 10,
                    transform: "translateY(-50%)",
                    boxShadow: "none",
                    lineHeight: 1,
                  }}
                  title="Clear"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                >
                  <MDBIcon icon="times" />
                </button>
              )}
            </div>
            <MDBIcon icon="calendar-alt" className="text-muted mr-2" />
            <select
              className="browser-default custom-select custom-select-sm"
              style={{ width: 130 }}
              value={year}
              onChange={({ target }) => setYear(target.value)}
              aria-label="Year"
            >
              {years.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
              <option value="">All years</option>
            </select>
          </div>
        </div>

        {!visible.length && (
          <div className="border rounded text-center text-muted py-5">
            {isLoading
              ? "Loading..."
              : `No received purchase orders here${year ? ` for ${year}` : ""}.`}
          </div>
        )}

        {visible.map((order, index) => {
          const isOpen = !!expanded[order._id];
          const status = STATUS[order.status];
          const percent = order.qty
            ? Math.round((order.available / order.qty) * 100)
            : 0;
          return (
            <div key={order._id} className="border rounded mb-2">
              <div
                role="button"
                tabIndex={0}
                className={`px-3 py-2 d-flex flex-wrap justify-content-between align-items-center ${
                  isOpen ? "blue lighten-5" : "grey lighten-5"
                }`}
                style={{ gap: 8, cursor: "pointer" }}
                onClick={() => toggle(order._id)}
                onKeyDown={(e) => e.key === "Enter" && toggle(order._id)}
              >
                <div className="d-flex align-items-center">
                  <MDBIcon
                    icon={isOpen ? "chevron-down" : "chevron-right"}
                    className="text-muted mr-3"
                    style={{ width: 12 }}
                  />
                  <div>
                    <b className="mr-2">
                      <span className="text-muted mr-1">{index + 1}.</span>
                      {poNumber(order)}
                    </b>
                    <MDBBadge color={status.color}>
                      <MDBIcon icon={status.icon} className="mr-1" />
                      {status.label}
                    </MDBBadge>
                    <small className="d-block text-muted">
                      Received {formatDate(order.received)}
                      {order.receivedBy?.fullName
                        ? ` by ${fullName(order.receivedBy.fullName)}`
                        : ""}
                      {order.userId?.fullName
                        ? ` · requested by ${fullName(order.userId.fullName)}`
                        : ""}
                    </small>
                  </div>
                </div>

                <div className="text-right" style={{ minWidth: 200 }}>
                  <small className="d-block">
                    <b>{percent}%</b> left · {order.items.length} item
                    {order.items.length === 1 ? "" : "s"}
                    {order.expired > 0 && (
                      <span className="text-danger ml-1">
                        · {order.expired} expired
                      </span>
                    )}
                  </small>
                  <div className="progress mt-1" style={{ height: 6 }}>
                    <div
                      className={`progress-bar bg-${
                        order.expired > 0 ? "danger" : "success"
                      }`}
                      role="progressbar"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              </div>

              {isOpen && (
                <>
                  {/* corrects this P.O. only */}
                  <div className="d-flex justify-content-end px-3 pt-2">
                    <MDBBtn
                      size="sm"
                      color="warning"
                      className="m-0 px-2 py-1"
                      disabled={!!regenerating}
                      title="Correct the inventory of this P.O. from its records"
                      onClick={() => handleRegenerate(order)}
                    >
                      <MDBIcon
                        icon="sync-alt"
                        spin={regenerating === order._id}
                        className="mr-1"
                      />
                      Regenerate P.O.
                    </MDBBtn>
                  </div>
                  <OrderItems order={order} status={STATUS} />
                </>
              )}
            </div>
          );
        })}
      </MDBCardBody>
    </MDBCard>
  );
}
